import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clientesAPI, facturacionAPI, usuariosAPI } from '../services/api';
import { ListadoScrollLayout } from '../components/ListadoScrollLayout';
import { formatFechaSistema } from '../utils/fechasSistema';
import * as listado from '../utils/facturacionListado.mjs';
import '../Clientes.css';
import './FacturacionClientes.css';

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

async function idsDesdeSesion() {
  const filtrosValores = listado.leerFiltrosGuardados();
  let columnasVisibles = {};
  let filtrosVisibles = {};
  try {
    const res = await usuariosAPI.getPreferencias(listado.clavePreferencias());
    const raw = res.data;
    const data = raw?.valor
      ?? (raw && typeof raw === 'object' && raw.columnasVisibles != null ? raw : null);
    if (data && typeof data === 'object') {
      if (data.columnasVisibles) columnasVisibles = data.columnasVisibles;
      if (data.filtrosVisibles) filtrosVisibles = data.filtrosVisibles;
    }
  } catch { /* sin preferencias: todos los filtros visibles */ }
  const visible = (id) => listado.filtroActivo(id, filtrosVisibles, columnasVisibles);
  const modo = listado.leerModoGuardado();
  const [clientesRes, candidatosRes] = await Promise.all([
    clientesAPI.getAll(listado.paramsConsultaClientes(filtrosValores, visible)),
    facturacionAPI.candidatos({ modo }),
  ]);
  if (!clientesRes.success) throw new Error(clientesRes.message || 'No se pudieron cargar los clientes');
  if (!candidatosRes.success) throw new Error(candidatosRes.message || 'No se pudieron cargar los movimientos');
  const clientesApi = clientesRes.data || [];
  const filas = listado.filasDeCandidatos(clientesApi, candidatosRes.data || [], filtrosValores, visible);
  return listado.idsClientesEmitidas({ clientesApi, filas, filtrosVisibles, columnasVisibles, filtrosValores });
}

const FacturasEmitidas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [facturas, setFacturas] = useState([]);
  const [hayAlguna, setHayAlguna] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await facturacionAPI.listar();
      if (!res.success) throw new Error(res.message || 'No se pudieron cargar las facturas');
      const todas = res.data || [];
      const snapshot = location.state?.clienteIds;
      const ids = Array.isArray(snapshot) ? snapshot : await idsDesdeSesion();
      const permitidos = new Set(ids.map((id) => Number(id)));
      setHayAlguna(todas.length > 0);
      setFacturas(todas.filter((f) => permitidos.has(Number(f.cliente_id))));
    } catch (error) {
      toast.error(error.message || 'No se pudieron cargar las facturas');
    } finally {
      setCargando(false);
    }
  }, [location.state]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirPdf = async (id) => {
    try {
      await facturacionAPI.abrirPdf(id);
    } catch (error) {
      toast.error(error.message || 'No se pudo abrir el PDF');
    }
  };

  const liberar = async (id) => {
    try {
      await facturacionAPI.liberar(id);
      await cargar();
    } catch (error) {
      toast.error(error.message || 'No se pudo liberar la factura');
    }
  };

  return (
    <ListadoScrollLayout
      className="clientes clientes-con-listado"
      header={(
        <div className="page-header">
          <h1>Facturas emitidas</h1>
          <div className="page-header-acciones">
            <button type="button" className="btn-secondary" onClick={() => navigate('/clientes/facturacion')}>
              Volver a facturar
            </button>
          </div>
        </div>
      )}
      fixedTop={(
        <p className="facturacion-emitidas-nota">Con los filtros de Facturación.</p>
      )}
      refreshing={cargando}
    >
      <table className="data-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Cliente</th>
            <th>Comprobante</th>
            <th>Total</th>
            <th>Estado</th>
            <th>CAE</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {facturas.length === 0 ? (
            <tr>
              <td colSpan={7} className="no-data">
                {cargando
                  ? 'Cargando…'
                  : (hayAlguna ? 'Ninguna factura emitida coincide con los filtros' : 'Todavía no hay facturas')}
              </td>
            </tr>
          ) : facturas.map((f) => (
            <tr key={f.id}>
              <td>{formatFechaSistema(f.fecha_emision)}</td>
              <td>
                {f.grupo_nombre && <span className="facturacion-grupo-tag">Grupo {f.grupo_nombre}</span>}
                {f.razon_social}
              </td>
              <td>{f.tipo_comprobante} {f.numero ? `${String(f.punto_venta).padStart(5, '0')}-${String(f.numero).padStart(8, '0')}` : ''}</td>
              <td>{formatMoneda(f.importe_total)}</td>
              <td>{f.estado}</td>
              <td>{f.cae || f.observaciones || ''}</td>
              <td>
                {f.estado === 'aprobada' && f.cae && (
                  <button type="button" className="btn-secondary" onClick={() => abrirPdf(f.id)}>PDF</button>
                )}
                {f.estado === 'pendiente' && !f.cae && (
                  <button type="button" className="btn-secondary" onClick={() => liberar(f.id)}>Liberar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListadoScrollLayout>
  );
};

export default FacturasEmitidas;
